import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, Grid, Box, 
  TextField, FormControl, InputLabel, Select, MenuItem,
  Button, LinearProgress, Divider, Snackbar, Alert,
  Stack,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useOperational } from 'providers/OperationalContextProvider'; 
import { useAuth } from 'providers/AuthContextProvider';
import { formatFirebaseDate } from 'utils/format';

export default function AppsForm({ open = false, onClose, data = null, onOrderUpdated }) {

    const { postApps, getServiceAccounts, postServiceAccounts, deleteServiceAccounts } = useOperational();
    const { user } = useAuth();
    const [snackbarState, setSnackbarState] = React.useState({ open: false, message: "", status: "info" });
    const [loading, setLoading] = React.useState(false);
    const [enabled, setEnabled] = React.useState(data?.enabled ?? true);
    const [platform, setPlatform] = React.useState('');
    const [serviceAccounts, setServiceAccounts] = React.useState([]);

    const columns =[
        { id: 'id', label: 'Id', defaultValue: data?.id ?? '', disabled: true, type: 'text', xs: 12, sm: 4, md: 4 },
        { id: 'app_id', label: 'App Id', defaultValue: data?.app_id ?? '', disabled: true, type: 'text', xs: 12, sm: 4, md: 4 },
        { id: 'name', label: 'Name', defaultValue: data?.name ?? '', disabled: false, type: 'text', xs: 12, sm: 4, md: 4 },
        { id: 'description', label: 'Description', defaultValue: data?.description ?? '', disabled: false, type: 'text', xs: 12, sm: 12, md: 12 },
        { id: 'created_at', label: 'Created At', defaultValue: formatFirebaseDate(data?.created_at), disabled: true, type: 'date', xs: 12, sm: 4, md: 4 },
        { id: 'updated_at', label: 'Update At', defaultValue: formatFirebaseDate(data?.updated_at), disabled: true, type: 'date', xs: 12, sm: 4, md: 4 },
    ];

    const handleCreateServiceAccount = async() => {
        try{
            const response = await postServiceAccounts({ client_email: user.email, app_id: data.app_id }); 
            if (response.success) {
                handleDownloadJson(response.data);
                setSnackbarState({ open: true, message: "Service Account Creada y Descargada. ¡Guarda el archivo!", status: "success" });
                fetchData()
            }
        } catch (err) {
            setSnackbarState({ open: true, message: err.message, status: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = (reason) => {
        if (reason === 'clickaway') return;
        setSnackbarState({ ...snackbarState, open: false });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const name = formData.get("name");
        const description =  formData.get("description");

        if (!name) return showError("name");
        if (!description) return showError("description");
        if (platform === null || platform === undefined) return showError("platform");
        if (enabled === null || enabled === undefined) return showError("enabled");
        
        const requestBody = {
            name: name,
            tenant_id: user.tenant_id,
            description: description,
            platform: platform,
            enabled: enabled,
        };
        setLoading(true);
        try {
            await postApps(requestBody)
            onOrderUpdated()
        } catch (err) {
            setSnackbarState({open:true, message: err.message, status: "error"})
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadJson = (data) => {
        // Convierte el objeto a un string JSON con indentación de 2 espacios
        const jsonString = JSON.stringify(data, null, 2);
        
        // Crea un objeto Blob con el contenido JSON
        const blob = new Blob([jsonString], { type: "application/json" });
        
        // Crea una URL temporal para el Blob
        const url = URL.createObjectURL(blob);
        
        // Crea un elemento <a> invisible para forzar la descarga
        const link = document.createElement('a');
        link.href = url;
        // Asigna el nombre del archivo (usa el client_id si existe, si no, un nombre genérico)
        link.download = `${data.client_id || 'service_account'}.json`;
        
        document.body.appendChild(link);
        link.click(); // Simula el clic
        
        // Limpieza
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const showError=(field)=> {
      setSnackbarState({ 
        open: true, 
        message: `Complete the required field: ${field}`, 
        status: 'warning' 
      });
      setLoading(false);
    }

    React.useEffect(()=>{
        if(!data)
            return
        fetchData()
    },[])

    const fetchData = async() => {
        try{
            const response = await getServiceAccounts({ app_id: data.app_id }); 
            setServiceAccounts(response.data)
        } catch (err) {
            setSnackbarState({ open: true, message: err.message, status: "error" });
        } finally {
            setLoading(false);
        }
    };

    return (
         <React.Fragment>
           <Dialog 
                open={open} 
                onClose={onClose} 
                maxWidth="md" 
                fullWidth
                scroll="paper"
            >
                <DialogTitle sx={{ m: 0, p: 2, pr: 6 }}>
                    {data ? "Editing App" : "Creating App"}
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                            '&:hover': {
                                color: 'error.main', 
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                {loading ? <LinearProgress /> : <Divider />}
                <DialogContent sx={{ p: 3 }}> 
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={12} md={12}>
                            <Box component="form" noValidate onSubmit={handleSubmit}>
                                <Grid container spacing={2}>
                                    {columns.map((column) => (
                                        <Grid item key={column.id} xs={column.xs} sm={column.sm} md={column.md}>
                                            <TextField
                                                disabled={column.disabled}
                                                size='small'
                                                required
                                                fullWidth
                                                id={column.id}
                                                name={column.id}
                                                defaultValue={column.defaultValue}
                                                type={column.type}
                                                hiddenLabel
                                                variant="outlined"
                                                aria-label={column.label}
                                                placeholder={column.label}
                                                InputProps={{
                                                    readOnly: column.disabled,
                                                }}
                                                inputProps={{
                                                    autoComplete: 'new-password', 
                                                }}
                                            />
                                        </Grid>
                                    ))}
                                    <Grid item xs={12} sm={4} md={4}>
                                        <FormControl fullWidth required size="small">
                                            <InputLabel id="status-label">Estado</InputLabel>
                                            <Select
                                                labelId="status-label"
                                                id="status"
                                                value={enabled}
                                                onChange={(e) => setEnabled(e.target.value)}
                                            >
                                                <MenuItem value={true}>Activo</MenuItem>
                                                <MenuItem value={false}>Inactivo</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={12}>
                                        <FormControl fullWidth required size="small">
                                            <InputLabel id="status-label">Platform</InputLabel>
                                            <Select
                                                labelId="status-label"
                                                id="platform"
                                                value={platform}
                                                onChange={(e) => setPlatform(e.target.value)}
                                            >
                                                <MenuItem value={'Android'}>Android</MenuItem>
                                                <MenuItem value={'iOS'}>iOS</MenuItem>
                                                <MenuItem value={'Web'}>Web</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                                <Button type="submit"  variant="contained" sx={{ mt: 3 }}>
                                    {data? "Update app": "create app"}
                                </Button>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={12} md={12}>
                            {data && (
                                <Box sx={{ mt: 4 }}>
                                    <Divider sx={{ mb: 2 }} />
                                    
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                        <Typography variant="h6">Service Accounts</Typography>
                                        <Button 
                                            variant="outlined" // O usa variant="add" si está en tu tema
                                            color="primary" 
                                            onClick={handleCreateServiceAccount}
                                            size="small"
                                        >
                                            Create Service Account
                                        </Button>
                                    </Stack>

                                    <List>
                                        {serviceAccounts.length === 0 ? (
                                            <Typography variant="body2" color="gray" align="center" sx={{ py: 2 }}>
                                                No hay service accounts vinculadas a esta App.
                                            </Typography>
                                        ) : (
                                            serviceAccounts.map((sa, index) => (
                                                <ListItem 
                                                    key={sa.id || index}
                                                    sx={{
                                                        border: '1px solid',
                                                        borderColor: 'grey.700',
                                                        borderRadius: '8px',
                                                        mb: 1,
                                                        backgroundColor: 'rgba(255,255,255,0.02)', // Estética sutil de tarjeta
                                                    }}
                                                    secondaryAction={
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <Chip 
                                                                size="small" 
                                                                label={sa.enabled ? "Activo" : "Inactivo"} 
                                                                color={sa.enabled ? "success" : "error"} 
                                                                variant="outlined"
                                                            />
                                                        </Stack>
                                                    }
                                                >
                                                    <ListItemText 
                                                        primary={
                                                            <Typography variant="body1" fontWeight="bold">
                                                                {sa.email}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography variant="body2" color="gray">
                                                                Created at: {formatFirebaseDate(sa.created_at)}
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItem>
                                            ))
                                        )}
                                    </List>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
            <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} open={snackbarState.open} onClose={handleClose}>
                <Alert onClose={handleClose} severity={snackbarState.status} variant="filled">
                    {snackbarState.message}
                </Alert>
            </Snackbar>
        </React.Fragment>
    );
}
